import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import BookingModal from './BookingModal';
import { useConcierge } from '@/hooks/useConcierge';

const QUICK_PROMPTS = [
  "I'm hungover",
  'Low energy lately',
  'Boost my immunity',
  'Skin glow & anti-aging',
  'Post-workout recovery',
  'Tell me about membership',
];

const BOOK_TAG = /\[\[BOOK:\s*([^\]]+)\]\]/i;

const parseReply = (text) => {
  const match = text.match(BOOK_TAG);
  const clean = text.replace(BOOK_TAG, '').trim();
  const services = match ? match[1].split(',').map((s) => s.trim()).filter(Boolean) : [];
  return { clean, services };
};

const Bubble = ({ msg, onBook }) => {
  const isUser = msg.role === 'user';
  const { clean, services } = isUser ? { clean: msg.content, services: [] } : parseReply(msg.content);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`concierge-msg-${msg.role}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-2xl rounded-br-sm'
            : 'bg-gray-800/80 text-gray-100 border border-yellow-500/20 rounded-2xl rounded-bl-sm'
        }`}
      >
        {clean || (msg.streaming ? <span className="animate-pulse">…</span> : '')}
        {services.length > 0 && !msg.streaming && (
          <button
            onClick={() => onBook(services)}
            className="mt-3 w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs font-bold px-4 py-2 rounded-full hover:from-yellow-500 hover:to-yellow-700 transition-all"
            data-testid="concierge-book-btn"
          >
            Book {services.join(' + ')}
          </button>
        )}
      </div>
    </div>
  );
};

const ConciergeWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [bookServices, setBookServices] = useState(null);
  const { messages, sending, send } = useConcierge();
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  if (location.pathname.startsWith('/emr') || location.pathname === '/qr-code') return null;

  const submit = (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setInput('');
    send(value);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold shadow-[0_0_30px_rgba(250,204,21,0.35)] hover:scale-105 transition-transform"
          data-testid="concierge-open-btn"
        >
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline">Ask the Concierge</span>
          <MessageCircle className="w-5 h-5 sm:hidden" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[min(600px,calc(100vh-5rem))] flex flex-col bg-gray-950 border border-yellow-500/40 rounded-3xl shadow-2xl overflow-hidden"
          data-testid="concierge-panel"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-yellow-500/20 bg-gradient-to-r from-gray-900 to-gray-950">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-yellow-400/15 border border-yellow-500/40 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-yellow-400" />
              </div>
              <div>
                <p className="font-gothic text-yellow-400 text-sm tracking-wide">EliteIV Concierge</p>
                <p className="text-[11px] text-gray-400">Wellness guidance · Instant answers</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-yellow-400" data-testid="concierge-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-4" data-testid="concierge-welcome">
                <div className="bg-gray-800/80 border border-yellow-500/20 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-gray-100">
                  Welcome to EliteIV Lounge. Tell me how you&apos;re feeling or what you want to achieve, and I&apos;ll match you with the right drip or shot.
                </div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => submit(p)}
                      className="text-xs px-3 py-1.5 rounded-full border border-yellow-500/40 text-yellow-300 hover:bg-yellow-400 hover:text-black transition-colors"
                      data-testid="concierge-quick-prompt"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <Bubble key={i} msg={m} onBook={setBookServices} />
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); submit(); }}
            className="flex items-center gap-2 px-4 py-3 border-t border-yellow-500/20 bg-gray-900"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about drips, prices, membership…"
              className="flex-1 bg-gray-800 text-white text-sm px-4 py-2.5 rounded-full border border-gray-700 focus:border-yellow-500 focus:outline-none"
              data-testid="concierge-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black flex items-center justify-center disabled:opacity-40 hover:from-yellow-500 hover:to-yellow-700 transition-all"
              data-testid="concierge-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <p className="text-[10px] text-gray-500 text-center pb-2 bg-gray-900">
            Not medical advice. A licensed provider clears every client before treatment.
          </p>
        </div>
      )}

      <BookingModal
        isOpen={bookServices !== null}
        onClose={() => setBookServices(null)}
        initialServices={bookServices || []}
      />
    </>
  );
};

export default ConciergeWidget;
